import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Grid, Typography, TextField, Button, MenuItem, Select,
  InputLabel, FormControl, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Card, CardContent, Avatar, Chip,
  Accordion, AccordionSummary, AccordionDetails, Divider, IconButton,
  CircularProgress, LinearProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuizIcon from '@mui/icons-material/Quiz';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagIcon from '@mui/icons-material/Flag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import InfoIcon from '@mui/icons-material/Info';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import GenerateVisualButton from '../../components/GenerateVisualButton';
import { adminHasPermission } from '../../utils/adminPermissions';
import VideoExplanationPlayer from '../../components/VideoExplanationPlayer';
import { PreviewLatex } from '../../components/PreviewLatex';
import QuestionImageUploader from '../../components/QuestionImageUploader';
import {
  QUILL_MODULES,
  QUILL_FORMATS,
  QuillR2Editor,
} from '../../utils/quillQuestionEditorConfig';

const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Hard'];

const defaultQuestion = {
  question: '',
  options: ['', '', '', ''],
  answer: '',
  duration: '',
  marks: '1',
  difficulty: 'Medium',
  explanation: ''
};

const stripEmptyQuill = (html) => {
  const value = String(html || '');
  const stripped = value.replace(/<p><br\s*\/?><\/p>/gi, '').trim();
  if (!stripped) return '';
  const textOnly = stripped
    .replace(/<img\b[^>]*>/gi, '__HAS_IMG__')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return textOnly ? value : '';
};

/** Matches CourseQuestion / whiteboard pipeline — while true, poll test data for status updates */
const IN_PROGRESS_VIDEO_STATUSES = ['pending', 'parsing', 'rendering', 'composing', 'uploading'];
const POLLING_VIDEO_STATUSES = ['parsing', 'rendering', 'composing', 'uploading'];

const makeAbsoluteUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  const u = url.trim();
  if (!u) return u;
  if (/^(https?:)?\/\//i.test(u) || /^data:/i.test(u) || /^blob:/i.test(u)) return u;
  const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '');
  if (!base) return u;
  if (u.startsWith('/')) return `${base}${u}`;
  return `${base}/${u}`;
};

const answerInOptionsTest = (answerPath, optionsPath) =>
  yup.string().test('answer-in-options', 'Answer must match one of the options', function (value) {
    const options = this.resolve(yup.ref(optionsPath)) || [];
    return options.includes(value);
  });

const validationSchema = yup.object().shape({
  academicCourseId: yup.string().required('Academic course is required'),
  subjectId: yup.string().required('Subject is required'),
  courseAssigmentId: yup.string().nullable(),
  topicIds: yup.array().min(1, 'At least one topic is required'),
  subtopicIds: yup.array().nullable(),
  questions: yup.array().min(1, 'At least one question is required').of(
    yup.object().shape({
      question: yup.string().required('Question is required').min(1).max(10000),
      options: yup.array().of(yup.string().required().min(1).max(2000)).min(2, 'Minimum 2 options required'),
      answer: answerInOptionsTest('answer', 'options').required('Answer is required'),
      duration: yup.number().typeError('Duration must be a number').required('Duration is required').min(1),
      marks: yup.number().typeError('Marks must be a number').nullable().transform((v, o) => (o === '' ? null : v)).min(0),
    })
  )
});

const CourseTest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const canCreateAssignments = adminHasPermission('create-assignments');
  const canUpdateAssignments = adminHasPermission('update-assignments');
  const canSave = isEditMode ? canUpdateAssignments : canCreateAssignments;
  const [dropdownData, setDropdownData] = useState({
    academicCourses: [], subjects: [], courseAssigments: [], topics: [], subtopics: []
  });
  const [loading, setLoading] = useState(false);
  const [fetchingTest, setFetchingTest] = useState(false);
  const [questionFlags, setQuestionFlags] = useState({});
  const [questionVisuals, setQuestionVisuals] = useState({});
  const [referenceBooks, setReferenceBooks] = useState([]);
  const [passageSourceSaving, setPassageSourceSaving] = useState({});
  const [testMeta, setTestMeta] = useState(null);
  const [unflaggingQuestion, setUnflaggingQuestion] = useState(null);
  const [flagDetailsDialog, setFlagDetailsDialog] = useState({ open: false, questionId: null, flags: [] });
  const [questionAgentVerification, setQuestionAgentVerification] = useState({});
  const [agentVerificationDialog, setAgentVerificationDialog] = useState({
    open: false,
    questionNumber: null,
    agentVerification: null,
  });
  const [regeneratingQuestionId, setRegeneratingQuestionId] = useState(null);
  const [questionVisualSaving, setQuestionVisualSaving] = useState({});
  const [deletingQuestionId, setDeletingQuestionId] = useState(null);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [questionDefinitions, setQuestionDefinitions] = useState([]);
  const fetchTestDataRef = useRef(null);
  /** When false, skip dependent-field clears (initial edit load / refetch). Create mode stays true. */
  const selectionCascadeReadyRef = useRef(false);
  const prevAcademicCourseIdRef = useRef(undefined);
  const prevSubjectIdRef = useRef(undefined);
  const prevCourseAssignmentIdRef = useRef(undefined);

  useEffect(() => {
    selectionCascadeReadyRef.current = !isEditMode;
    if (!isEditMode) {
      prevAcademicCourseIdRef.current = undefined;
      prevSubjectIdRef.current = undefined;
      prevCourseAssignmentIdRef.current = undefined;
    }
  }, [isEditMode]);

  const resolveVisualAsset = (visualImageUrl, visual) => {
    const urlCandidate = [
      visualImageUrl,
      visual?.url,
      visual?.r2_url,
      visual?.public_url,
      visual?.image_url,
      visual?.src,
    ].find((value) => typeof value === 'string' && value.trim().length > 0);

    if (urlCandidate) {
      return { type: 'image', src: urlCandidate, alt: visual?.alt };
    }

    if (visual?.svg) {
      return { type: 'svg', svg: visual.svg, alt: visual?.alt };
    }

    return null;
  };

  const {
    register, handleSubmit, control, watch, reset, setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      academicCourseId: '', subjectId: '', courseAssigmentId: '', topicIds: [], subtopicIds: [],
      questions: [defaultQuestion]
    },
    resolver: yupResolver(validationSchema)
  });

  const { fields, append, remove, replace } = useFieldArray({ control, name: 'questions' });
  const watchAll = watch();

  const subjectSelectOptions = useMemo(() => {
    const base = dropdownData.subjects || [];
    const sid = watchAll.subjectId;
    if (!sid) return base;
    const has = base.some((o) => String(o.id) === String(sid));
    if (!has && testMeta?.subject_name) {
      return [{ id: sid, name: testMeta.subject_name }, ...base];
    }
    return base;
  }, [dropdownData.subjects, watchAll.subjectId, testMeta?.subject_name]);

  const assignmentSelectOptions = useMemo(() => {
    const base = dropdownData.courseAssigments || [];
    const aid = watchAll.courseAssigmentId;
    if (!aid) return base;
    const has = base.some((o) => String(o.id) === String(aid));
    if (!has && testMeta?.assignment_label) {
      return [{ id: aid, name: testMeta.assignment_label }, ...base];
    }
    return base;
  }, [dropdownData.courseAssigments, watchAll.courseAssigmentId, testMeta?.assignment_label]);

  const topicSelectOptions = useMemo(() => {
    const base = dropdownData.topics || [];
    const selectedIds = Array.isArray(watchAll.topicIds) ? watchAll.topicIds : [];
    if (!selectedIds.length) return base;
    const baseIds = new Set(base.map((o) => String(o.id)));
    const missing = selectedIds.filter((id) => !baseIds.has(String(id)));
    if (!missing.length) return base;
    const fallbacks = missing.map((id) => ({ id: String(id), name: `Topic ${id}` }));
    return [...fallbacks, ...base];
  }, [dropdownData.topics, watchAll.topicIds]);

  const subtopicSelectOptions = useMemo(() => {
    const base = dropdownData.subtopics || [];
    const selectedIds = Array.isArray(watchAll.subtopicIds) ? watchAll.subtopicIds : [];
    if (!selectedIds.length) return base;
    const baseIds = new Set(base.map((o) => String(o.id)));
    const missing = selectedIds.filter((id) => !baseIds.has(String(id)));
    if (!missing.length) return base;
    const fallbacks = missing.map((id) => ({ id: String(id), name: `Subtopic ${id}` }));
    return [...fallbacks, ...base];
  }, [dropdownData.subtopics, watchAll.subtopicIds]);

  const totalMarks = (watchAll.questions || []).reduce(
    (sum, q) => sum + (parseFloat(q.marks) || 0), 0
  );
  const marksOver100 = totalMarks > 100;

  useEffect(() => {
    api.get('admin/ca_records')
      .then(res => setDropdownData(prev => ({ ...prev, academicCourses: res.data.data || [] })))
      .catch(() => toast.error('Failed to fetch academic courses'));
  }, []);

  useEffect(() => {
    if (!watchAll.academicCourseId) {
      setDropdownData(prev => ({
        ...prev,
        subjects: [],
        courseAssigments: [],
        topics: [],
        subtopics: [],
      }));
      return;
    }
    api.get(`admin/ca_based_weeks_subjects/${watchAll.academicCourseId}`)
      .then(res => {
        const raw = res.data?.data;
        const payload = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
        const subjects = payload.subjects ?? [];
        const assignments = payload.assignments ?? [];
        setDropdownData(prev => ({
          ...prev,
          subjects,
          courseAssigments: assignments,
        }));
      })
      .catch(() => toast.error('Failed to fetch subjects/assignments'));
  }, [watchAll.academicCourseId]);

  useEffect(() => {
    if (!watchAll.subjectId || !watchAll.academicCourseId) {
      return;
    }
    api.get(`admin/fetch/topics/by-academic-subject/${watchAll.academicCourseId}/${watchAll.subjectId}`)
      .then(res => setDropdownData(prev => ({ ...prev, topics: res.data.data || [] })))
      .catch(() => toast.error('Failed to fetch topics'));
  }, [watchAll.subjectId, watchAll.academicCourseId]);

  useEffect(() => {
    const ids = Array.isArray(watchAll.topicIds) ? watchAll.topicIds.filter(Boolean) : [];
    if (!ids.length) {
      setDropdownData(prev => ({ ...prev, subtopics: [] }));
      return;
    }
    Promise.all(ids.map(id => api.get(`admin/fetch/topics/subtopics/${id}`).then(r => r.data.data || []).catch(() => [])))
      .then(results => {
        const seen = new Set();
        const merged = results.flat().filter(o => {
          const k = String(o.id);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setDropdownData(prev => ({ ...prev, subtopics: merged }));
      });
  }, [JSON.stringify(watchAll.topicIds)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Changing course / subject / duration (assignment) must clear downstream selections or the old
  // topic_id is still submitted — only the topic row links the test to an assignment.
  useEffect(() => {
    if (!selectionCascadeReadyRef.current) return;
    const v = watchAll.academicCourseId;
    if (prevAcademicCourseIdRef.current === undefined) {
      prevAcademicCourseIdRef.current = v;
      return;
    }
    if (prevAcademicCourseIdRef.current !== v) {
      setValue('subjectId', '');
      setValue('courseAssigmentId', '');
      setValue('topicIds', []);
      setValue('subtopicIds', []);
      prevSubjectIdRef.current = '';
      prevCourseAssignmentIdRef.current = '';
      prevAcademicCourseIdRef.current = v;
    }
  }, [watchAll.academicCourseId, setValue]);

  useEffect(() => {
    if (!selectionCascadeReadyRef.current) return;
    const v = watchAll.subjectId;
    if (prevSubjectIdRef.current === undefined) {
      prevSubjectIdRef.current = v;
      return;
    }
    if (prevSubjectIdRef.current !== v) {
      setValue('courseAssigmentId', '');
      setValue('topicIds', []);
      setValue('subtopicIds', []);
      prevCourseAssignmentIdRef.current = '';
      prevSubjectIdRef.current = v;
    }
  }, [watchAll.subjectId, setValue]);

  useEffect(() => {
    if (!selectionCascadeReadyRef.current) return;
    const v = watchAll.courseAssigmentId;
    if (prevCourseAssignmentIdRef.current === undefined) {
      prevCourseAssignmentIdRef.current = v;
      return;
    }
    if (prevCourseAssignmentIdRef.current !== v) {
      setValue('topicIds', []);
      setValue('subtopicIds', []);
      prevCourseAssignmentIdRef.current = v;
    }
  }, [watchAll.courseAssigmentId, setValue]);

  // Fetch test data for edit mode (fetchTestDataRef.current is callable from Regenerate/Delete handlers)
  useEffect(() => {
    if (isEditMode && id) {
      fetchTestDataRef.current = async () => {
        setFetchingTest(true);
        selectionCascadeReadyRef.current = false;
        try {
          const apiUrl = `admin/assign/test/${id}`;
          const response = await api.get(apiUrl);
          if (response.data.success) {
            const testData = response.data.data;
            setTestMeta({
              topic_name: testData.topic_name ?? null,
              subtopic_name: testData.subtopic_name ?? null,
              subject_name: testData.subject_name ?? null,
              assignment_label: testData.assignment_label ?? null,
              exam_board: testData.exam_board ?? null,
              difficulty: testData.difficulty ?? null,
              ai_settings: testData.ai_settings && typeof testData.ai_settings === 'object' ? testData.ai_settings : null,
            });

            const toIdStr = (v) => (v != null && v !== '' ? String(v) : '');

            let academicCourseId = toIdStr(testData.academic_course_id);
            let subjectId = toIdStr(testData.subject_id);
            let courseAssigmentId = toIdStr(
              testData.course_assigment_id ?? testData.course_assignment_id
            );
            const topicIdStrs = Array.isArray(testData.master_topic_ids) && testData.master_topic_ids.length
              ? testData.master_topic_ids.map(String)
              : (Array.isArray(testData.topic_ids) && testData.topic_ids.length
                  ? testData.topic_ids.map(String)
                  : (testData.topic_id ? [String(testData.topic_id)] : []));
            const subtopicIdStrs = Array.isArray(testData.master_subtopic_ids) && testData.master_subtopic_ids.length
              ? testData.master_subtopic_ids.map(String)
              : (Array.isArray(testData.subtopic_ids) && testData.subtopic_ids.length
                  ? testData.subtopic_ids.map(String)
                  : (testData.subtopic_id ? [String(testData.subtopic_id)] : []));

            if ((!academicCourseId || !subjectId || !courseAssigmentId) && testData.course_topic_id) {
              try {
                const topicRes = await api.get(`admin/assign/topic/subtopic/${testData.course_topic_id}`);
                if (topicRes.data.success) {
                  const topicData = topicRes.data.data;
                  if (!subjectId) {
                    subjectId = toIdStr(topicData.subject_id);
                  }
                  if (!courseAssigmentId) {
                    courseAssigmentId = toIdStr(
                      topicData.course_assigment_id ?? topicData.course_assignment_id
                    );
                  }
                  const assignmentPk =
                    topicData.course_assigment_id ?? topicData.course_assignment_id;
                  if (!academicCourseId && assignmentPk) {
                    const assignmentRes = await api.get(`admin/assign/assignment/${assignmentPk}`);
                    if (assignmentRes.data.success) {
                      const assignment = assignmentRes.data.data;
                      academicCourseId = toIdStr(
                        assignment.acdemic_course_id ?? assignment.academic_course_id
                      );
                    }
                  }
                }
              } catch (err) {
                console.error('Error fetching topic details:', err);
              }
            }

            // For AI-created tests (course_topic_id = null), fall back to the first
            // course_test_assignments pivot row for academic course + week.
            if (!academicCourseId || !courseAssigmentId) {
              const firstAsgn = Array.isArray(testData.assignments) && testData.assignments[0];
              if (firstAsgn) {
                if (!academicCourseId) academicCourseId = toIdStr(firstAsgn.academic_course_id);
                if (!courseAssigmentId) courseAssigmentId = toIdStr(firstAsgn.course_assignment_id);
              }
            }

            let transformedQuestions = [defaultQuestion];
            if (testData.questions && Array.isArray(testData.questions)) {
              const visualsArr = Array.isArray(testData.question_visuals) ? testData.question_visuals : [];
              transformedQuestions = testData.questions.map((question, idx) => ({
                question: question || '',
                options: testData.options && testData.options[idx]
                  ? testData.options[idx]
                  : ['', '', '', ''],
                answer: testData.answers && testData.answers[idx]
                  ? testData.answers[idx]
                  : '',
                duration: testData.duration_in_sec && testData.duration_in_sec[idx]
                  ? String(testData.duration_in_sec[idx])
                  : '30',
                marks: testData.marks && testData.marks[idx] != null
                  ? String(testData.marks[idx])
                  : '1',
                difficulty: (testData.difficulties && testData.difficulties[idx])
                  || testData.difficulty
                  || 'Medium',
                explanation: visualsArr[idx]?.explanation ?? ''
              }));

              if (testData.question_flags && Array.isArray(testData.question_flags) && testData.question_ids) {
                const flagsMap = {};
                testData.question_flags.forEach((flagInfo, idx) => {
                  flagsMap[idx] = flagInfo;
                });
                setQuestionFlags(flagsMap);
              }

              if (testData.question_agent_verification && Array.isArray(testData.question_agent_verification)) {
                const avMap = {};
                testData.question_agent_verification.forEach((row, idx) => {
                  avMap[idx] = row;
                });
                setQuestionAgentVerification(avMap);
              } else {
                setQuestionAgentVerification({});
              }

              if (testData.question_visuals && Array.isArray(testData.question_visuals)) {
                const visualsMap = {};
                testData.question_visuals.forEach((visualInfo, idx) => {
                  visualsMap[idx] = visualInfo;
                });
                setQuestionVisuals(visualsMap);
              }

              setQuestionTypes(Array.isArray(testData.question_types) ? testData.question_types : []);
              setQuestionDefinitions(Array.isArray(testData.question_definitions) ? testData.question_definitions : []);
            }

            reset({
              academicCourseId,
              subjectId,
              courseAssigmentId,
              topicIds: topicIdStrs,
              subtopicIds: subtopicIdStrs,
              questions: transformedQuestions,
            }, { keepDefaultValues: false });

            prevAcademicCourseIdRef.current = undefined;
            prevSubjectIdRef.current = undefined;
            prevCourseAssignmentIdRef.current = undefined;
          }
        } catch (err) {
          console.error('Error fetching test:', err);
          if (err.response?.status === 404) {
            toast.error('Test not found');
            navigate('/admin/test-list');
          } else {
            toast.error('Failed to load test data');
          }
        } finally {
          setFetchingTest(false);
          selectionCascadeReadyRef.current = true;
        }
      };
      fetchTestDataRef.current();
    }
  }, [id, isEditMode, setValue, reset, navigate]);

  // Passages carry the book they were taken from so students see the attribution.
  // The registered reference books are the authority on how a title is spelled.
  useEffect(() => {
    if (!isEditMode) return undefined;
    let cancelled = false;
    api
      .get('admin/master-form/reference-books-for-ai', { params: { subject: 'english' } })
      .then((res) => {
        if (!cancelled) setReferenceBooks(res?.data?.data || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isEditMode]);

  const savePassageSource = async (idx, bookId) => {
    const qId = questionVisuals[idx]?.question_id;
    if (!qId) return;
    const book = referenceBooks.find((b) => b.id === bookId);
    const passageSource = book ? { title: book.title, author: book.author || null } : null;
    setPassageSourceSaving((prev) => ({ ...prev, [idx]: true }));
    try {
      await api.put(`admin/ai/question/${qId}/passage`, { passage_source: passageSource });
      setQuestionVisuals((prev) => ({
        ...prev,
        [idx]: {
          ...prev[idx],
          ai_metadata: { ...(prev[idx]?.ai_metadata || {}), passage_source: passageSource || undefined },
        },
      }));
      toast.success(book ? `Source set to "${book.title}"` : 'Source attribution removed');
    } catch {
      toast.error('Could not save the passage source');
    } finally {
      setPassageSourceSaving((prev) => ({ ...prev, [idx]: false }));
    }
  };

  useEffect(() => {
    if (!isEditMode || !id) return;
    const hasInProgress = Object.values(questionVisuals).some(
      (v) => v && POLLING_VIDEO_STATUSES.includes(v.video_generation_status)
    );
    if (!hasInProgress) return;
    const pollTimer = setInterval(async () => {
      if (typeof fetchTestDataRef.current === 'function') {
        await fetchTestDataRef.current();
      }
    }, 5000);
    return () => clearInterval(pollTimer);
  }, [isEditMode, id, questionVisuals]);

  const handleCSVUpload = (e) => {
    if (!canSave) {
      toast.error('You do not have permission to bulk-upload questions.');
      e.target.value = '';
      return;
    }
    const file = e.target.files[0];
    if (!file || !file.name.endsWith('.csv')) {
      toast.error('Only .csv files are allowed.');
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors = [];
        const parsed = results.data.map((row, index) => {
          const options = [row.option1, row.option2, row.option3, row.option4].filter(Boolean);
          const answer = row.answer;
          const duration = row.duration;
          const marks = row.marks;

          if (options.length < 2) errors.push(`Row ${index + 2}: Minimum 2 options required.`);
          if (!answer || !options.includes(answer)) errors.push(`Row ${index + 2}: Answer must match one of the options.`);
          if (!duration || isNaN(duration)) errors.push(`Row ${index + 2}: Duration must be a valid number.`);
          if (marks !== undefined && marks !== '' && isNaN(marks)) errors.push(`Row ${index + 2}: Marks must be a valid number.`);

          return {
            question: row.question || '',
            options,
            answer,
            duration: parseInt(duration || 1),
            marks: marks !== undefined && marks !== '' ? String(parseFloat(marks)) : '1'
          };
        });

        if (errors.length > 0) {
          toast.error(`CSV Validation Failed:\n${errors.join('\n')}`);
          return;
        }

        replace(parsed);
        toast.success(`${parsed.length} questions loaded from CSV`);
      },
      error: () => toast.error('Failed to parse CSV')
    });
  };

  const onSubmit = async (data) => {
    setLoading(true);
    if (!canSave) {
      setLoading(false);
      toast.error('You do not have permission to save this worksheet.');
      return;
    }
    if (totalMarks > 100) {
      setLoading(false);
      toast.error(`Total marks (${totalMarks}) exceed 100. Please reduce marks before saving.`);
      return;
    }

    // Prepare request body according to API documentation
    // For update (PUT): use JSON format
    // For create (POST): use FormData (existing implementation)
    const isUpdate = isEditMode;
    
    let requestData;
    let config = {};
    
    if (isUpdate) {
      // Update: Use JSON format as per API documentation
      const questions = data.questions.map(q => q.question);
      const options = data.questions.map(q => q.options);
      const answers = data.questions.map(q => q.answer);
      const duration_in_sec = data.questions.map(q => parseInt(q.duration) || 30);
      const marks = data.questions.map(q => parseFloat(q.marks) || 1);
      const difficulties = data.questions.map(q => q.difficulty || null);
      const explanations = data.questions.map(q => stripEmptyQuill(q.explanation) || null);

      const academicCourseIdNum = data.academicCourseId !== '' && data.academicCourseId != null ? parseInt(String(data.academicCourseId), 10) : NaN;
      const courseAssignmentIdNum = data.courseAssigmentId !== '' && data.courseAssigmentId != null ? parseInt(String(data.courseAssigmentId), 10) : NaN;
      const topicIdsArr = (Array.isArray(data.topicIds) ? data.topicIds : []).map(Number).filter(Boolean);
      const subtopicIdsArr = (Array.isArray(data.subtopicIds) ? data.subtopicIds : []).map(Number).filter(Boolean);
      requestData = {
        academic_course_id: Number.isFinite(academicCourseIdNum) ? academicCourseIdNum : undefined,
        course_assignment_id: Number.isFinite(courseAssignmentIdNum) ? courseAssignmentIdNum : undefined,
        topic_ids: topicIdsArr.length ? topicIdsArr : undefined,
        subtopic_ids: subtopicIdsArr.length ? subtopicIdsArr : undefined,
        master_topic_ids: topicIdsArr.length ? topicIdsArr : [],
        master_subtopic_ids: subtopicIdsArr.length ? subtopicIdsArr : [],
        questions: questions.length > 0 ? questions : undefined,
        options: options.length > 0 ? options : undefined,
        answers: answers.length > 0 ? answers : undefined,
        duration_in_sec: duration_in_sec.length > 0 ? duration_in_sec : undefined,
        marks: marks.length > 0 ? marks : undefined,
        difficulties: difficulties.length > 0 ? difficulties : undefined,
        explanations: explanations.length > 0 ? explanations : undefined
      };
      
      // Remove undefined fields
      Object.keys(requestData).forEach(key => {
        if (requestData[key] === undefined) {
          delete requestData[key];
        }
      });
      
      config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };
    } else {
      // Create: Use FormData (existing implementation)
      const formData = new FormData();
      if (data.academicCourseId) formData.append('academic_course_id', data.academicCourseId);
      if (data.courseAssigmentId) formData.append('course_assignment_id', data.courseAssigmentId);
      const createTopicIds = (Array.isArray(data.topicIds) ? data.topicIds : []).map(Number).filter(Boolean);
      const createSubtopicIds = (Array.isArray(data.subtopicIds) ? data.subtopicIds : []).map(Number).filter(Boolean);
      if (createTopicIds.length) {
        createTopicIds.forEach((id, i) => formData.append(`topic_ids[${i}]`, id));
        formData.append('topic_id', createTopicIds[0]);
      }
      if (createSubtopicIds.length) {
        createSubtopicIds.forEach((id, i) => formData.append(`subtopic_ids[${i}]`, id));
        formData.append('subtopic_id', createSubtopicIds[0]);
      }

      data.questions.forEach((q, i) => {
        formData.append(`questions[${i}]`, q.question);
        q.options.forEach((opt, j) => formData.append(`options[${i}][${j}]`, opt));
        formData.append(`answers[${i}]`, q.answer);
        formData.append(`duration_in_sec[${i}]`, q.duration);
        formData.append(`marks[${i}]`, parseFloat(q.marks) || 1);
      });
      
      requestData = formData;
    }

    try {
      const endpoint = isEditMode 
        ? `admin/assign/test/${id}` 
        : 'admin/assign/test';
      const method = isEditMode ? 'put' : 'post';

      await api[method](endpoint, requestData, config);
      toast.success(isEditMode ? 'Worksheet updated successfully!' : 'Worksheet created successfully!');
      
      if (isEditMode) {
        navigate('/admin/test-list');
      } else {
        reset({ academicCourseId: '', subjectId: '', courseAssigmentId: '', topicIds: [], subtopicIds: [], questions: [defaultQuestion] });
        setTimeout(() => {
          navigate('/admin/test-list');
        }, 1500);
      }
    } catch (err) {
      console.error('Test save error:', err.response || err);
      
      if (err.response?.status === 422) {
        const errors = err.response.data.errors;
        if (errors) {
          Object.entries(errors).forEach(([field, messages]) => {
            const message = Array.isArray(messages) ? messages[0] : messages;
            toast.error(`${field}: ${message}`);
          });
        } else {
          toast.error(err.response.data?.message || 'Validation error occurred');
        }
      } else if (err.response?.status === 404) {
        toast.error('Test not found');
        if (isEditMode) {
          navigate('/admin/test-list');
        }
      } else if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(err?.response?.data?.message || (isEditMode ? 'Failed to update test' : 'Submission failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnflagQuestion = async (questionId, questionIndex) => {
    if (!id) return;
    if (!canUpdateAssignments) {
      toast.error('You do not have permission to unflag questions.');
      return;
    }

    setUnflaggingQuestion(questionId);
    try {
      const response = await api.delete(`admin/assign/test/${id}/questions/${questionId}/unflag`);
      if (response.data.success) {
        toast.success(response.data.message || 'Question unflagged successfully');
        // Update flagging state
        setQuestionFlags(prev => ({
          ...prev,
          [questionIndex]: {
            ...prev[questionIndex],
            is_flagged: false,
            flag_count: 0,
            flags: []
          }
        }));
      }
    } catch (err) {
      console.error('Error unflagging question:', err);
      toast.error(err?.response?.data?.message || 'Failed to unflag question');
    } finally {
      setUnflaggingQuestion(null);
    }
  };

  const handleViewFlagDetails = (questionIndex) => {
    const flagInfo = questionFlags[questionIndex];
    if (flagInfo && flagInfo.flags && flagInfo.flags.length > 0) {
      setFlagDetailsDialog({
        open: true,
        questionId: flagInfo.question_id,
        flags: flagInfo.flags,
        questionNumber: questionIndex + 1
      });
    }
  };

  const handleViewAgentVerificationDetails = (questionIndex) => {
    const row = questionAgentVerification[questionIndex];
    const av = row?.agent_verification;
    if (row?.has_agent_verification && av) {
      setAgentVerificationDialog({
        open: true,
        questionNumber: questionIndex + 1,
        agentVerification: av,
      });
    }
  };

  const agentVerificationStatusMeta = (status) => {
    if (status === 'passed') return { label: 'Passed', color: 'success' };
    if (status === 'issues_found') return { label: 'Issues found', color: 'error' };
    if (status === 'needs_human_review') return { label: 'Needs review', color: 'warning' };
    return { label: status || 'Unknown', color: 'default' };
  };

  if (fetchingTest) {
    return (
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        minHeight: '100vh',
        p: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading test data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh',
      p: 3
    }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            width: 56,
            height: 56
          }}>
            <QuizIcon sx={{ fontSize: 28, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5
            }}>
              {isEditMode ? 'Edit Worksheet' : 'Worksheet Creator'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode
                ? 'Update worksheet questions and settings'
                : 'Create engaging worksheets with multiple choice questions'
              }
            </Typography>
          </Box>
        </Box>
      </Box>

      {isEditMode && testMeta && (testMeta.topic_name || testMeta.subtopic_name || testMeta.subject_name || testMeta.exam_board || testMeta.difficulty || (testMeta.ai_settings && (testMeta.ai_settings.strand || testMeta.ai_settings.subSubject))) && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
          <CardContent sx={{ py: 1.5, px: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Sub topic / metadata</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {testMeta.subject_name && <Chip size="small" label={testMeta.subject_name} />}
              {testMeta.topic_name && <Chip size="small" label={testMeta.topic_name} variant="outlined" />}
              {testMeta.subtopic_name && <Chip size="small" label={testMeta.subtopic_name} variant="outlined" />}
              {(() => {
                const ai = testMeta.ai_settings || {};
                const strand = typeof ai.strand === 'string' ? ai.strand.trim() : '';
                const subSubject = typeof ai.subSubject === 'string' ? ai.subSubject.trim() : '';
                const showStrand = strand && (strand !== (testMeta.topic_name || '') || strand.includes(','));
                const showSubSubject = subSubject && subSubject !== strand
                  && (subSubject !== (testMeta.subtopic_name || '') || subSubject.includes(','));
                return (
                  <>
                    {showStrand && (
                      <Chip size="small" label={`AI strand: ${strand}`} variant="outlined" color="info" />
                    )}
                    {showSubSubject && (
                      <Chip size="small" label={`AI sub-focus: ${subSubject}`} variant="outlined" color="info" />
                    )}
                  </>
                );
              })()}
              {testMeta.exam_board && <Chip size="small" label={`Exam board: ${testMeta.exam_board}`} color="primary" variant="outlined" />}
              {testMeta.difficulty && <Chip size="small" label={`Difficulty: ${testMeta.difficulty}`} color="secondary" variant="outlined" />}
            </Box>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Course Selection Card */}
        <Card sx={{ 
          mb: 4,
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ 
                background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                width: 48,
                height: 48
              }}>
                <SchoolIcon sx={{ color: 'white' }} />
              </Avatar>
              <Typography variant="h6" fontWeight={600}>
                Course & Topic Selection
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {[
                { field: 'academicCourseId', label: 'Academic Course', icon: '🎓', multiple: false },
                { field: 'subjectId', label: 'Subject', icon: '📚', multiple: false },
                { field: 'courseAssigmentId', label: 'Week / assignment', icon: '📝', multiple: false },
                { field: 'topicIds', label: 'Topic', icon: '📖', multiple: true },
                { field: 'subtopicIds', label: 'Subtopic', icon: '📄', multiple: true }
              ].map(({ field, label, icon, multiple }) => {
                const opts = field === 'subtopicIds'
                  ? subtopicSelectOptions
                  : field === 'topicIds'
                    ? topicSelectOptions
                    : field === 'subjectId'
                      ? subjectSelectOptions
                      : field === 'courseAssigmentId'
                        ? assignmentSelectOptions
                        : (dropdownData.academicCourses || []);
                const fieldError = errors[field];
                return (
                <Grid item xs={12} sm={6} key={field}>
                  <FormControl fullWidth error={!!fieldError}>
                    <InputLabel sx={{
                      background: 'white',
                      px: 1,
                      '&.Mui-focused': { color: '#667eea' }
                    }}>
                      {icon} {label}
                    </InputLabel>
                    <Controller
                      name={field}
                      control={control}
                      render={({ field: controllerField }) => (
                        <Select
                          label={`${icon} ${label}`}
                          multiple={multiple}
                          value={multiple
                            ? (Array.isArray(controllerField.value) ? controllerField.value : [])
                            : (controllerField.value ?? '')}
                          onChange={(e) => controllerField.onChange(e.target.value)}
                          onBlur={controllerField.onBlur}
                          inputRef={controllerField.ref}
                          name={controllerField.name}
                          renderValue={multiple
                            ? (selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(selected || []).map((val) => {
                                    const opt = opts.find((o) => String(o.id) === String(val));
                                    return <Chip key={val} label={opt ? opt.name : val} size="small" />;
                                  })}
                                </Box>
                              )
                            : undefined}
                          sx={{
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(102, 126, 234, 0.3)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea'
                            }
                          }}
                        >
                          {opts.map((opt) => (
                            <MenuItem key={String(opt.id)} value={String(opt.id)}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 480 }}>
                                <PreviewLatex html={opt.name} />
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors?.[field]?.message && (
                      <Typography color="error" fontSize={12} sx={{ mt: 0.5, ml: 1 }}>
                        {errors[field].message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              ); })}
            </Grid>
          </CardContent>
        </Card>

        {/* CSV Upload Card */}
        <Card sx={{ 
          mb: 4,
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ 
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                width: 48,
                height: 48
              }}>
                <UploadFileIcon sx={{ color: 'white' }} />
              </Avatar>
              <Typography variant="h6" fontWeight={600}>
                Bulk Question Upload
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                component="label"
                disabled={!canSave}
                startIcon={<UploadFileIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)'
                  }
                }}
              >
                Upload CSV File
                <input type="file" hidden accept=".csv" onChange={handleCSVUpload} />
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  const sample = `question,option1,option2,option3,option4,answer,duration,marks
What is the capital of France?,Paris,Lyon,Marseille,India,Paris,30,2
What is 2 + 2?,3,4,5,6,4,20,1
Which planet is closest to the Sun?,Mercury,Venus,Earth,Mars,Mercury,25,2
What is the largest ocean on Earth?,Atlantic,Pacific,Indian,Arctic,Pacific,30,2
Who wrote Romeo and Juliet?,Charles Dickens,William Shakespeare,Mark Twain,Jane Austen,William Shakespeare,35,3
What is the chemical symbol for water?,H2O,CO2,NaCl,O2,H2O,15,1
How many continents are there?,5,6,7,8,7,20,1
What is the square root of 64?,6,7,8,9,8,25,2
Which gas do plants absorb from the atmosphere?,Oxygen,Nitrogen,Carbon Dioxide,Hydrogen,Carbon Dioxide,30,2
What is the capital of Japan?,Beijing,Seoul,Tokyo,Bangkok,Tokyo,20,2`;
                  const blob = new Blob([sample], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'sample_test_format.csv');
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                sx={{
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#f57c00',
                    background: 'rgba(255, 152, 0, 0.1)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Download Sample
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* CSV Format Preview */}
        <Card sx={{ 
          mb: 4,
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ 
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                width: 48,
                height: 48
              }}>
                <Typography sx={{ fontSize: '20px' }}>📄</Typography>
              </Avatar>
              <Typography variant="h6" fontWeight={600}>
                CSV Format Preview
              </Typography>
            </Box>

            <TableContainer component={Paper} sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                    {['question', 'option1', 'option2', 'option3', 'option4', 'answer', 'duration', 'marks'].map(header => (
                      <TableCell key={header} sx={{ fontWeight: 600, color: '#495057' }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ '&:hover': { background: 'rgba(102, 126, 234, 0.05)' } }}>
                    <TableCell>What is the capital of France?</TableCell>
                    <TableCell>Paris</TableCell>
                    <TableCell>Lyon</TableCell>
                    <TableCell>Marseille</TableCell>
                    <TableCell>India</TableCell>
                    <TableCell>
                      <Chip label="Paris" size="small" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label="30 sec" size="small" sx={{ background: '#e3f2fd', color: '#1976d2' }} />
                    </TableCell>
                    <TableCell>
                      <Chip label="2" size="small" sx={{ background: '#f3e5f5', color: '#7b1fa2' }} />
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ '&:hover': { background: 'rgba(102, 126, 234, 0.05)' } }}>
                    <TableCell>What is 2 + 2?</TableCell>
                    <TableCell>3</TableCell>
                    <TableCell>4</TableCell>
                    <TableCell>5</TableCell>
                    <TableCell>6</TableCell>
                    <TableCell>
                      <Chip label="4" size="small" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label="20 sec" size="small" sx={{ background: '#e3f2fd', color: '#1976d2' }} />
                    </TableCell>
                    <TableCell>
                      <Chip label="1" size="small" sx={{ background: '#f3e5f5', color: '#7b1fa2' }} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Questions Section */}
        <Card sx={{ 
          mb: 4,
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ 
                background: 'linear-gradient(135deg, #e91e63 0%, #ad1457 100%)',
                width: 48,
                height: 48
              }}>
                <QuizIcon sx={{ color: 'white' }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Worksheet Questions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fields.length} question{fields.length !== 1 ? 's' : ''} added
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!canSave}
                onClick={() => append(defaultQuestion)}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Add Question
              </Button>
            </Box>

            {/* Total marks progress bar */}
            <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: marksOver100 ? 'error.main' : 'divider', bgcolor: marksOver100 ? 'rgba(244,67,54,0.04)' : 'background.paper' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>Total Marks</Typography>
                <Typography variant="subtitle2" fontWeight={700} color={marksOver100 ? 'error.main' : totalMarks === 100 ? 'success.main' : 'text.primary'}>
                  {totalMarks} / 100
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((totalMarks / 100) * 100, 100)}
                color={marksOver100 ? 'error' : totalMarks >= 90 ? 'warning' : 'primary'}
                sx={{ borderRadius: 1, height: 8 }}
              />
              {marksOver100 && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  ⚠ Total exceeds 100 marks — reduce marks before saving.
                </Typography>
              )}
            </Box>

            {fields.map((field, idx) => (
              <Accordion 
                key={field.id}
                sx={{ 
                  mb: 2,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  '&:before': { display: 'none' },
                  background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    borderRadius: 3,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 2
                    }
                  }}
                >
                  <Avatar sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    width: 32,
                    height: 32,
                    fontSize: '14px',
                    fontWeight: 600
                  }}>
                    {idx + 1}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Question {idx + 1}
                      </Typography>
                      {questionTypes[idx] && questionTypes[idx] !== 'multiple_choice' && (
                        <Chip
                          size="small"
                          label={questionTypes[idx].replace(/_/g, ' ')}
                          sx={{ fontSize: 11, height: 20, textTransform: 'capitalize' }}
                        />
                      )}
                    </Box>
                    {watchAll.questions?.[idx]?.question ? (
                      <Box sx={{ mt: 0.25, maxWidth: '100%', '& .MathJax': { fontSize: '0.8rem !important' } }}>
                        <PreviewLatex html={watchAll.questions[idx].question} sx={{ '& > div': { fontSize: '0.75rem', lineHeight: 1.45 } }} />
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Enter your question...
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(idx);
                    }}
                    disabled={fields.length === 1 || !canSave}
                    sx={{ 
                      color: '#f44336',
                      '&:hover': { background: 'rgba(244, 67, 54, 0.1)' }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </AccordionSummary>
                
                <AccordionDetails sx={{ pt: 0 }}>
                  <Grid container spacing={3}>
                    {isEditMode && questionVisuals[idx]?.passage_text && (
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, bgcolor: '#faf8f5', borderRadius: 2, borderLeft: '4px solid #8d6e63' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#5d4037', textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
                            Passage
                          </Typography>
                          {(() => {
                            const currentTitle = questionVisuals[idx]?.ai_metadata?.passage_source?.title || '';
                            const currentBook = referenceBooks.find((b) => b.title === currentTitle);
                            return (
                              <FormControl size="small" fullWidth sx={{ mb: 1.5, maxWidth: 520 }}>
                                <InputLabel>Source book (shown to students)</InputLabel>
                                <Select
                                  label="Source book (shown to students)"
                                  value={currentBook ? currentBook.id : ''}
                                  disabled={!canSave || passageSourceSaving[idx]}
                                  onChange={(e) => savePassageSource(idx, e.target.value)}
                                >
                                  <MenuItem value=""><em>No attribution</em></MenuItem>
                                  {referenceBooks.map((b) => (
                                    <MenuItem key={b.id} value={b.id}>
                                      {b.is_primary ? '★ ' : ''}{b.title}{b.author ? ` — ${b.author}` : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            );
                          })()}
                          <PreviewLatex html={questionVisuals[idx].passage_text} />
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ p: 0 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', display: 'block', mb: 0.5 }}>
                          📝 Question Text
                        </Typography>
                        <Box
                          sx={{
                            '& .ql-editor': { minHeight: 90, background: '#fff', fontSize: '0.95rem' },
                            '& .ql-editor img': { maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 1, my: 1 },
                            '& .MathJax': { fontSize: '0.9rem !important' },
                            border: errors?.questions?.[idx]?.question ? '1px solid #d32f2f' : undefined,
                            borderRadius: 2,
                          }}
                        >
                          <Controller
                            name={`questions.${idx}.question`}
                            control={control}
                            render={({ field }) => (
                              <QuillR2Editor
                                theme="snow"
                                value={field.value || ''}
                                onChange={field.onChange}
                                modules={QUILL_MODULES}
                                formats={QUILL_FORMATS}
                                style={{ background: '#fff' }}
                                showMathTools
                              />
                            )}
                          />
                        </Box>
                        {errors?.questions?.[idx]?.question?.message && (
                          <Typography color="error" fontSize={12} sx={{ mt: 0.5, ml: 1 }}>
                            {errors.questions[idx].question.message}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Visual Display - Only in Edit Mode */}
                    {isEditMode && questionVisuals[idx] && (
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, borderRadius: 2, background: '#f5f5f5', border: '1px solid rgba(0,0,0,0.1)' }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            Visual Reference
                          </Typography>
                          {(() => {
                            const visualAsset = resolveVisualAsset(
                              questionVisuals[idx].visual_image_url,
                              questionVisuals[idx].visual
                            );
                            return visualAsset ? (
                              <Box sx={{ p: 1, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', background: '#fff', maxWidth: 500, overflow: 'hidden', mb: 1.5 }}>
                                {visualAsset.type === 'image' ? (
                                  <img
                                    alt={visualAsset.alt || 'Question visual'}
                                    src={makeAbsoluteUrl(visualAsset.src)}
                                    style={{ width: '100%', height: 'auto', display: 'block' }}
                                  />
                                ) : (
                                  <Box
                                    component="div"
                                    role="img"
                                    aria-label={visualAsset.alt || 'Question visual'}
                                    sx={{ width: '100%', '& svg': { width: '100%', height: 'auto', display: 'block' } }}
                                    dangerouslySetInnerHTML={{ __html: visualAsset.svg }}
                                  />
                                )}
                              </Box>
                            ) : null;
                          })()}
                          {canUpdateAssignments && (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Replace image
                                </Typography>
                                <GenerateVisualButton
                                  questionText={watchAll.questions?.[idx]?.question || ''}
                                  subject={testMeta?.subject_name || ''}
                                  subjectId={watchAll.subjectId || null}
                                  visual={questionVisuals[idx]?.visual || null}
                                  disabled={!!questionVisualSaving[idx]}
                                  onGenerated={async (url, spec) => {
                                    const qId = questionVisuals[idx]?.question_id;
                                    if (!qId) return;
                                    setQuestionVisualSaving((prev) => ({ ...prev, [idx]: true }));
                                    try {
                                      await api.patch(`admin/assign/test/${id}/questions/${qId}/visual`, { image_url: url });
                                      setQuestionVisuals((prev) => ({
                                        ...prev,
                                        [idx]: { ...prev[idx], visual_image_url: url, visual: spec || { url, image_url: url } },
                                      }));
                                    } catch {
                                      toast.error('Generated the image but could not save it');
                                    } finally {
                                      setQuestionVisualSaving((prev) => ({ ...prev, [idx]: false }));
                                    }
                                  }}
                                />
                              </Box>
                              <QuestionImageUploader
                                value={questionVisuals[idx]?.visual_image_url || ''}
                                disabled={!!questionVisualSaving[idx]}
                                onChange={async (url) => {
                                  const qId = questionVisuals[idx]?.question_id;
                                  if (!qId) return;
                                  setQuestionVisualSaving((prev) => ({ ...prev, [idx]: true }));
                                  try {
                                    await api.patch(`admin/assign/test/${id}/questions/${qId}/visual`, { image_url: url || null });
                                    setQuestionVisuals((prev) => ({
                                      ...prev,
                                      [idx]: { ...prev[idx], visual_image_url: url || null, visual: url ? { url, image_url: url } : null },
                                    }));
                                    toast.success(url ? 'Image updated' : 'Image removed');
                                  } catch {
                                    toast.error('Failed to update image');
                                  } finally {
                                    setQuestionVisualSaving((prev) => ({ ...prev, [idx]: false }));
                                  }
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    )}

                    {isEditMode && questionVisuals[idx] && (
                      <>
                        {(questionVisuals[idx].ai_metadata && typeof questionVisuals[idx].ai_metadata === 'object' && !Array.isArray(questionVisuals[idx].ai_metadata) && Object.keys(questionVisuals[idx].ai_metadata).length > 0) && (
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>AI metadata:</Typography>
                              {['strand', 'subSubject', 'subject', 'exam_board'].map((key) => {
                                const val = questionVisuals[idx].ai_metadata[key];
                                return val != null && val !== '' ? <Chip key={key} size="small" label={`${key}: ${val}`} sx={{ height: 22 }} /> : null;
                              })}
                            </Box>
                          </Grid>
                        )}
                        {questionVisuals[idx].ai_reference && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Reference: </Typography>
                            <Typography variant="body2" component="span">{questionVisuals[idx].ai_reference}</Typography>
                          </Grid>
                        )}
                        {(() => {
                          const v = questionVisuals[idx];
                          const qId = v.question_id;
                          const status = v.video_generation_status;
                          const videoBusy = IN_PROGRESS_VIDEO_STATUSES.includes(status);
                          const statusLabel = status ? String(status).replace(/_/g, ' ') : '';
                          return (
                            <>
                              {videoBusy && (
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="primary" fontWeight={600}>
                                      {status === 'pending'
                                        ? 'Video queued — waiting in queue…'
                                        : `Generating video (${statusLabel})…`}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              {v.explanation_video_url && !videoBusy && (
                                <Grid item xs={12}>
                                  <VideoExplanationPlayer
                                    videoUrl={makeAbsoluteUrl(v.explanation_video_url)}
                                    questionText={watchAll?.questions?.[idx]?.question ?? ''}
                                  />
                                </Grid>
                              )}
                              {status === 'failed' && !videoBusy && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="error">
                                    Video generation failed. Use Regenerate to try again.
                                  </Typography>
                                </Grid>
                              )}
                              {qId && canUpdateAssignments && (
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      disabled={
                                        videoBusy ||
                                        regeneratingQuestionId === qId
                                      }
                                      onClick={async () => {
                                        setRegeneratingQuestionId(qId);
                                        try {
                                          await api.post(`admin/assign/test/${id}/questions/${qId}/regenerate-video`);
                                          toast.success('Video regeneration queued');
                                          if (fetchTestDataRef.current) await fetchTestDataRef.current();
                                        } catch (err) {
                                          toast.error(err?.response?.data?.message || 'Failed to queue regeneration');
                                        } finally {
                                          setRegeneratingQuestionId(null);
                                        }
                                      }}
                                    >
                                      {regeneratingQuestionId === qId ? 'Regenerating…' : 'Regenerate video'}
                                    </Button>
                                    {v.explanation_video_url && !videoBusy && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        disabled={deletingQuestionId === qId}
                                        onClick={async () => {
                                          setDeletingQuestionId(qId);
                                          try {
                                            await api.delete(`admin/assign/test/${id}/questions/${qId}/explanation-video`);
                                            toast.success('Video removed');
                                            if (fetchTestDataRef.current) await fetchTestDataRef.current();
                                          } catch (err) {
                                            toast.error(err?.response?.data?.message || 'Failed to remove video');
                                          } finally {
                                            setDeletingQuestionId(null);
                                          }
                                        }}
                                      >
                                        {deletingQuestionId === qId ? 'Removing…' : 'Delete video'}
                                      </Button>
                                    )}
                                  </Box>
                                </Grid>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}

                    {isEditMode && (
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', border: '1px solid rgba(0,0,0,0.08)' }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            Explanation (rich text — supports images)
                          </Typography>
                          <Box
                            sx={{
                              '& .ql-editor': { minHeight: 140, background: '#fff' },
                              '& .ql-editor img': { maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 1, my: 1 },
                              '& .MathJax': { fontSize: '0.85rem !important' },
                            }}
                          >
                            <Controller
                              name={`questions.${idx}.explanation`}
                              control={control}
                              render={({ field }) => (
                                <QuillR2Editor
                                  theme="snow"
                                  value={field.value || ''}
                                  onChange={field.onChange}
                                  modules={QUILL_MODULES}
                                  formats={QUILL_FORMATS}
                                  style={{ background: '#fff' }}
                                  showMathTools
                                />
                              )}
                            />
                          </Box>
                          {watchAll.questions?.[idx]?.explanation && stripEmptyQuill(watchAll.questions[idx].explanation) && (
                            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(25,118,210,0.06)', border: '1px solid rgba(25,118,210,0.2)' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                Preview
                              </Typography>
                              <PreviewLatex html={watchAll.questions[idx].explanation} />
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    )}
                    
                    {(() => {
                      const qType = (questionTypes[idx] || 'multiple_choice').toLowerCase();
                      const isShortAnswer = qType === 'short_answer' || qType.includes('short');
                      const isFillBlanks = qType === 'fill_blanks' || (qType.includes('fill') && qType.includes('blank'));
                      const isVocabMatching = qType === 'vocab_matching' || qType.includes('vocab') || qType.includes('match');
                      const isWritingComposition = qType === 'writing_composition' || (qType.includes('writing') && qType.includes('compos'));

                      if (isWritingComposition) {
                        return (
                          <Grid item xs={12}>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#e0f2fe', borderLeft: '4px solid #0284c7' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                ✏️ Writing Composition Topic
                              </Typography>
                              <Typography variant="body2" color="text.secondary">Options and answer fields are not applicable for writing composition questions.</Typography>
                            </Box>
                          </Grid>
                        );
                      }

                      if (isVocabMatching) {
                        const defs = questionDefinitions[idx] || [];
                        const optCount = Math.max(watchAll.questions?.[idx]?.options?.length || 0, defs.length, 1);
                        return (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Vocabulary Matching — Words &amp; Definitions
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>Words</Typography>
                              {Array.from({ length: optCount }).map((_, optIdx) => (
                                <TextField
                                  key={optIdx}
                                  fullWidth
                                  size="small"
                                  label={`Word ${optIdx + 1}`}
                                  {...register(`questions.${idx}.options.${optIdx}`)}
                                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                              ))}
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>Definitions (read-only)</Typography>
                              {defs.length > 0 ? defs.map((def, defIdx) => (
                                <Box key={defIdx} sx={{ mb: 1, p: 1.2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', minHeight: 40 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                    <Typography component="span" variant="body2">{String.fromCharCode(65 + defIdx)}.</Typography>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <PreviewLatex html={def || '—'} />
                                    </Box>
                                  </Box>
                                </Box>
                              )) : (
                                <Typography variant="caption" color="text.secondary">No definitions stored.</Typography>
                              )}
                            </Grid>
                          </>
                        );
                      }

                      if (isShortAnswer || isFillBlanks) {
                        return (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label={isFillBlanks ? '✅ Answer (word that fills the blank)' : '✅ Correct Answer'}
                              {...register(`questions.${idx}.answer`)}
                              error={!!errors?.questions?.[idx]?.answer}
                              helperText={errors?.questions?.[idx]?.answer?.message}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(76, 175, 80, 0.3)' },
                                  '&:hover fieldset': { borderColor: '#4caf50' },
                                  '&.Mui-focused fieldset': { borderColor: '#4caf50' }
                                }
                              }}
                            />
                          </Grid>
                        );
                      }

                      // Default: multiple_choice and all other types
                      return (
                        <>
                          {[0, 1, 2, 3].map(optIdx => (
                            <Grid item xs={12} sm={6} key={optIdx}>
                              <TextField
                                fullWidth
                                label={`${String.fromCharCode(65 + optIdx)}. Option ${optIdx + 1}`}
                                {...register(`questions.${idx}.options.${optIdx}`)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: 'rgba(102, 126, 234, 0.3)' },
                                    '&:hover fieldset': { borderColor: '#667eea' },
                                    '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                  }
                                }}
                              />
                            </Grid>
                          ))}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="✅ Correct Answer"
                              {...register(`questions.${idx}.answer`)}
                              error={!!errors?.questions?.[idx]?.answer}
                              helperText={errors?.questions?.[idx]?.answer?.message}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(76, 175, 80, 0.3)' },
                                  '&:hover fieldset': { borderColor: '#4caf50' },
                                  '&.Mui-focused fieldset': { borderColor: '#4caf50' }
                                }
                              }}
                            />
                          </Grid>
                        </>
                      );
                    })()}
                    
                    <Grid item xs={12} sm={4}>
                      <TextField 
                        fullWidth 
                        label="⏱️ Duration (seconds)"
                        type="number" 
                        {...register(`questions.${idx}.duration`)} 
                        error={!!errors?.questions?.[idx]?.duration} 
                        helperText={errors?.questions?.[idx]?.duration?.message}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': { borderColor: 'rgba(255, 152, 0, 0.3)' },
                            '&:hover fieldset': { borderColor: '#ff9800' },
                            '&.Mui-focused fieldset': { borderColor: '#ff9800' }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="🏅 Marks"
                        type="number"
                        inputProps={{ min: 0, step: 0.5 }}
                        {...register(`questions.${idx}.marks`)}
                        error={!!errors?.questions?.[idx]?.marks}
                        helperText={errors?.questions?.[idx]?.marks?.message}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': { borderColor: 'rgba(123, 31, 162, 0.3)' },
                            '&:hover fieldset': { borderColor: '#7b1fa2' },
                            '&.Mui-focused fieldset': { borderColor: '#7b1fa2' }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <Controller
                        name={`questions.${idx}.difficulty`}
                        control={control}
                        defaultValue="Medium"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            label="🎯 Difficulty"
                            value={field.value || 'Medium'}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(33, 150, 243, 0.3)' },
                                '&:hover fieldset': { borderColor: '#2196f3' },
                                '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                              }
                            }}
                          >
                            {DIFFICULTY_OPTIONS.map((opt) => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>

                    {/* Flagging Information Section - Only in Edit Mode */}
                    {isEditMode && questionFlags[idx] && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          background: questionFlags[idx].is_flagged 
                            ? 'rgba(255, 152, 0, 0.1)' 
                            : 'rgba(76, 175, 80, 0.1)',
                          border: `1px solid ${questionFlags[idx].is_flagged ? 'rgba(255, 152, 0, 0.3)' : 'rgba(76, 175, 80, 0.3)'}`
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            {questionFlags[idx].is_flagged ? (
                              <FlagIcon sx={{ color: '#ff9800' }} />
                            ) : (
                              <FlagOutlinedIcon sx={{ color: '#4caf50' }} />
                            )}
                            <Typography variant="subtitle2" fontWeight={600}>
                              Flagging Status
                            </Typography>
                            {questionFlags[idx].is_flagged && (
                              <Chip 
                                label={`${questionFlags[idx].flag_count} flag${questionFlags[idx].flag_count !== 1 ? 's' : ''}`}
                                size="small"
                                color="warning"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                            {!questionFlags[idx].is_flagged && (
                              <Chip 
                                label="No flags"
                                size="small"
                                color="success"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          </Box>
                          
                          {questionFlags[idx].is_flagged && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                              <Tooltip title="View flagging details">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<InfoIcon />}
                                  onClick={() => handleViewFlagDetails(idx)}
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: '#ff9800',
                                    color: '#ff9800',
                                    '&:hover': {
                                      borderColor: '#f57c00',
                                      background: 'rgba(255, 152, 0, 0.1)'
                                    }
                                  }}
                                >
                                  View Details
                                </Button>
                              </Tooltip>
                              <Tooltip title="Unflag this question after correcting it">
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="warning"
                                  startIcon={unflaggingQuestion === questionFlags[idx].question_id ? <CircularProgress size={16} /> : <FlagOutlinedIcon />}
                                  onClick={() => handleUnflagQuestion(questionFlags[idx].question_id, idx)}
                                  disabled={!canUpdateAssignments || unflaggingQuestion === questionFlags[idx].question_id}
                                  sx={{
                                    textTransform: 'none',
                                    '&:hover': {
                                      background: '#f57c00'
                                    }
                                  }}
                                >
                                  {unflaggingQuestion === questionFlags[idx].question_id ? 'Unflagging...' : 'Unflag All'}
                                </Button>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    )}

                    {/* Agent verification (AI verify batch — same spirit as student flags) */}
                    {isEditMode && questionAgentVerification[idx]?.has_agent_verification && questionAgentVerification[idx]?.agent_verification && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            background:
                              questionAgentVerification[idx].agent_verification.overall_status === 'passed'
                                ? 'rgba(33, 150, 243, 0.08)'
                                : questionAgentVerification[idx].agent_verification.overall_status === 'issues_found'
                                ? 'rgba(244, 67, 54, 0.08)'
                                : 'rgba(255, 152, 0, 0.1)',
                            border: `1px solid ${
                              questionAgentVerification[idx].agent_verification.overall_status === 'passed'
                                ? 'rgba(33, 150, 243, 0.35)'
                                : questionAgentVerification[idx].agent_verification.overall_status === 'issues_found'
                                ? 'rgba(244, 67, 54, 0.35)'
                                : 'rgba(255, 152, 0, 0.35)'
                            }`,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                            <FactCheckIcon
                              sx={{
                                color:
                                  questionAgentVerification[idx].agent_verification.overall_status === 'passed'
                                    ? '#1976d2'
                                    : questionAgentVerification[idx].agent_verification.overall_status === 'issues_found'
                                    ? '#f44336'
                                    : '#ff9800',
                              }}
                            />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Verify agent (AI)
                            </Typography>
                            <Chip
                              size="small"
                              color={agentVerificationStatusMeta(questionAgentVerification[idx].agent_verification.overall_status).color}
                              label={agentVerificationStatusMeta(questionAgentVerification[idx].agent_verification.overall_status).label}
                              sx={{ fontWeight: 600 }}
                            />
                            {questionAgentVerification[idx].agent_verification.verified_at && (
                              <Typography variant="caption" color="text.secondary">
                                Recorded {new Date(questionAgentVerification[idx].agent_verification.verified_at).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Answer: ${questionAgentVerification[idx].agent_verification.answer_verdict?.status || '—'}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Visual: ${questionAgentVerification[idx].agent_verification.visual_verdict?.status || '—'}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Stem: ${questionAgentVerification[idx].agent_verification.question_text_verdict?.status || '—'}`}
                            />
                            {Array.isArray(questionAgentVerification[idx].agent_verification.issues) &&
                              questionAgentVerification[idx].agent_verification.issues.length > 0 && (
                              <Chip
                                size="small"
                                color="warning"
                                label={`${questionAgentVerification[idx].agent_verification.issues.length} issue(s)`}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Tooltip title="View full agent verification rationales and issues">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<InfoIcon />}
                                onClick={() => handleViewAgentVerificationDetails(idx)}
                                sx={{
                                  textTransform: 'none',
                                  borderColor: '#1976d2',
                                  color: '#1976d2',
                                  '&:hover': {
                                    borderColor: '#1565c0',
                                    background: 'rgba(25, 118, 210, 0.08)',
                                  },
                                }}
                              >
                                View details
                              </Button>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>

        {/* Submit Section */}
        <Box sx={{ display: 'flex', mt: 4 }}>
          <Button 
            type="submit" 
            variant="contained"
            size="large"
            disabled={loading || fetchingTest || !canSave}
            startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
            endIcon={!loading && <ArrowForwardIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 4,
              px: 6,
              py: 2,
              fontSize: '16px',
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                transform: 'translateY(-3px)',
                boxShadow: '0 16px 45px rgba(102, 126, 234, 0.5)'
              },
              '&:disabled': {
                opacity: 0.6,
              },
              '&:active': {
                transform: 'translateY(-1px)'
              }
            }}
          >
            {loading 
              ? (isEditMode ? 'Updating...' : 'Creating...')
              : (isEditMode ? 'Update Worksheet' : 'Create Worksheet')
            }
          </Button>
        </Box>
      </form>

      {/* Flag Details Dialog */}
      <Dialog 
        open={flagDetailsDialog.open} 
        onClose={() => setFlagDetailsDialog({ open: false, questionId: null, flags: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <FlagIcon />
          Flagging Details - Question {flagDetailsDialog.questionNumber}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {flagDetailsDialog.flags.length > 0 ? (
            <List>
              {flagDetailsDialog.flags.map((flag, index) => (
                <React.Fragment key={flag.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Flagged by: {flag.user_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {flag.user_email}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Student: {flag.student_name} ({flag.student_email})
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Flagged on: {new Date(flag.flagged_at).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                  {index < flagDetailsDialog.flags.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">No flagging details available.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setFlagDetailsDialog({ open: false, questionId: null, flags: [] })}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agent verification details (AI Test Creator verify batch) */}
      <Dialog
        open={agentVerificationDialog.open}
        onClose={() => setAgentVerificationDialog({ open: false, questionNumber: null, agentVerification: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FactCheckIcon />
          Verify agent — Question {agentVerificationDialog.questionNumber}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {agentVerificationDialog.agentVerification ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Status: {agentVerificationDialog.agentVerification.overall_status || '—'}
              </Typography>
              {agentVerificationDialog.agentVerification.verified_at && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {new Date(agentVerificationDialog.agentVerification.verified_at).toLocaleString()}
                </Typography>
              )}
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>Answer</Typography>
                <PreviewLatex html={agentVerificationDialog.agentVerification.answer_verdict?.rationale || '—'} />
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>Visual</Typography>
                <PreviewLatex html={agentVerificationDialog.agentVerification.visual_verdict?.rationale || '—'} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>Stem</Typography>
                <PreviewLatex html={agentVerificationDialog.agentVerification.question_text_verdict?.rationale || '—'} />
              </Box>
              {Array.isArray(agentVerificationDialog.agentVerification.issues) &&
                agentVerificationDialog.agentVerification.issues.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Issues
                  </Typography>
                  <List dense>
                    {agentVerificationDialog.agentVerification.issues.map((issue, i) => (
                      <ListItem key={issue.composite_key || issue.key || i} sx={{ alignItems: 'flex-start', py: 1 }}>
                        <ListItemText
                          primary={(
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                size="small"
                                label={issue.severity || 'issue'}
                                color={issue.severity === 'high' ? 'error' : 'warning'}
                              />
                              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                                <PreviewLatex html={issue.message || ''} inline />
                              </Box>
                            </Box>
                          )}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">No verification data.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setAgentVerificationDialog({ open: false, questionNumber: null, agentVerification: null })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseTest;
